import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * normalize: strips hyphens, spaces, dots, slashes → lowercase
 * e.g. "VT-56" → "vt56", so typing "VT56" matches it
 */
const normalize = (str) =>
  String(str)
    .toLowerCase()
    .replace(/[\s\-_./\\]/g, '');

/**
 * SearchableSelect — keyboard-friendly searchable dropdown.
 * 
 * Props:
 *   value        — current selected value
 *   onValueChange — callback(value)
 *   options      — [{ value, label }]
 *   placeholder  — string
 *   disabled     — bool
 *   className    — extra classes on the trigger button
 *   contentClassName — extra classes on the dropdown panel
 */
const SearchableSelect = ({
  value,
  onValueChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  className = '',
  contentClassName = '',
  'data-testid': dataTestId,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const selectedOption = options.find(o => o.value === value);

  // Fuzzy/normalized filter
  const filtered = query.trim()
    ? options.filter(o =>
        normalize(o.label).includes(normalize(query)) ||
        normalize(String(o.value)).includes(normalize(query))
      )
    : options;

  const handleSelect = (optValue) => {
    onValueChange(optValue);
    setOpen(false);
    setQuery('');
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Keyboard: Escape closes
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)} onKeyDown={handleKeyDown} data-testid={dataTestId}>
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm ring-offset-background',
          'focus:outline-none focus:ring-1 focus:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'h-9',
          className
        )}
        data-testid="searchable-select-trigger"
      >
        <span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={cn('ml-1 h-3.5 w-3.5 shrink-0 opacity-50 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full min-w-[12rem] rounded-md border bg-popover shadow-lg',
            contentClassName
          )}
        >
          {/* Search input */}
          <div className="flex items-center border-b px-2 py-1.5">
            <Search className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to search..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid="searchable-select-input"
            />
          </div>

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">No results found</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleSelect(o.value)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                    o.value === value && 'bg-accent/50 font-medium'
                  )}
                  data-testid={`searchable-option-${o.value}`}
                >
                  <Check className={cn('h-3.5 w-3.5 shrink-0', o.value === value ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{o.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
